import LoadingSpinner from '@/components/LoadingSpinner';
import { accessibilityDataAtom } from '@/global-store';
import { useFetchPageDetailReport } from '@http/queries';
import { Box } from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import IssueExportForm from '../IssueExportForm';
import {
  buildJiraAdfTable,
  headingAdf,
  subHeadingWithTextAdf,
} from './adfUtils';

export function PagesExport({
  todosToExport,
  jiraProject,
  jiraUsers,
  linkedJiraUserID,
  issueExportFormRef,
}) {
  const { t } = useTranslation();

  const [accessibilityData] = useAtom(accessibilityDataAtom);
  const conformanceTarget = accessibilityData?.conformanceTarget;

  const pageIndex = todosToExport[0].props.pageIndex;

  const {
    data: pageReport,
    isLoading,
    isError: pageReportLoadError,
  } = useFetchPageDetailReport(pageIndex);

  const tableRow = useMemo(() => {
    if (!pageReport || pageReportLoadError) return [];

    const _tableRows = [];

    for (const [ruleId, ruleDetails] of Object.entries(pageReport.rules)) {
      if (!ruleDetails?.sources?.length > 0) continue;

      let occurrences = 0;

      const eaRule = accessibilityData.eaRules[ruleId];
      const wcagChapter = eaRule.categories[0];
      const wcagLvl = accessibilityData.wcagRules[wcagChapter].wcagLvl;
      const title = accessibilityData.eaRules[ruleId]?.content || '(no name)';

      let errorInfo = {};
      ruleDetails.sources.forEach((source, index) => {
        if (!source) return;
        occurrences++;
        const { xPath, metaData } = source;
        if (!errorInfo.html && metaData?.outerHtml)
          errorInfo.html = metaData.outerHtml;
      });

      if (conformanceTarget.includes(wcagLvl)) {
        const row = [title, (occurrences || 1).toString(), wcagLvl];
        if (errorInfo.html && errorInfo.html.length < 500) {
          row.push(errorInfo.html);
        }
        _tableRows.push(row);
      }
    }

    const _sortedTopRow = _tableRows.sort((a, b) => +b[1] - +a[1]).slice(0, 10);

    return _sortedTopRow;
  }, [
    accessibilityData,
    pageIndex,
    pageReport,
    pageReportLoadError,
    conformanceTarget,
  ]);

  const writtenURL = useMemo(() => {
    if (isLoading) return '';
    else return pageReport?.page ?? pageReport?.context?.pageUrl;
  }, [pageReport, isLoading]);

  const calculatedAuditScore = useMemo(() => {
    if (!writtenURL) return 0;

    return accessibilityData?.calcOverallAuditScore(
      accessibilityData?.auditScores?.perPage[writtenURL],
      false,
    );
  }, [accessibilityData, writtenURL]);

  if (!jiraUsers || isLoading) return <LoadingSpinner />;

  const totalErrorsOnPage = writtenURL
    ? accessibilityData?.calcTotalwarningsForConformanceTarget(
        accessibilityData?.report?.summary?.summaryPerPage?.[writtenURL],
      )
    : 0;

  const headers = [
    t('title'),
    t('occurrences'),
    t('supportSelectCategory'),
    'HTML',
  ];

  const jiraBodyDescripiton = [];

  // Title
  jiraBodyDescripiton.push(
    headingAdf(pageReport?.title || t('noPageTitle'), 2),
  );

  // Description

  jiraBodyDescripiton.push(
    subHeadingWithTextAdf(`${t('page')} ${t('url')}`, writtenURL, writtenURL),
  );

  jiraBodyDescripiton.push(
    subHeadingWithTextAdf(
      t('checkNow'),
      pageReport?.title || t('noPageTitle'),
      writtenURL + '#runWCAG',
    ),
  );
  jiraBodyDescripiton.push(
    subHeadingWithTextAdf(t('pageRating'), `${calculatedAuditScore} %`),
  );
  jiraBodyDescripiton.push(
    subHeadingWithTextAdf(
      t('totalErrorsForPage'),
      totalErrorsOnPage.toString(),
    ),
  );

  // All failed rules on this page
  if (+totalErrorsOnPage > 0) {
    const tableAdf = buildJiraAdfTable(headers, tableRow);
    if (JSON.stringify(tableAdf).length < 25000) {
      jiraBodyDescripiton.push(headingAdf(t('top10RulesFailedOnThisPage'), 3));
      jiraBodyDescripiton.push(tableAdf);
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <IssueExportForm
        ref={issueExportFormRef}
        todosToExport={todosToExport}
        jiraProject={jiraProject}
        jiraBodyDescripiton={jiraBodyDescripiton}
        jiraUsers={jiraUsers}
        linkedJiraUserID={linkedJiraUserID}
      />
    </Box>
  );
}
